import { Suspense, lazy, useEffect } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';
import { useBillingAccess } from '../services/tenantQueries';
import { SETTINGS_ROUTES, PROFILE_ROUTE } from '../lib/constants/settingsRoutes';
import { RouteSkeleton } from './RouteSkeleton';

// Lazy: BlockedAccessPage pulls in PlansSection (packages, purchase dialogs). SubscriptionGuard
// itself sits in the main bundle (it wraps every tenant route), so a static import here would
// drag that whole subtree into every user's initial load for a screen only a Blocked tenant ever
// sees (INEFFECTIVE_DYNAMIC_IMPORT build warning confirmed this).
const BlockedAccessPage = lazy(() => import('../pages/tenant/settings/billing/BlockedAccessPage'));

// Poll interval while a tenant is Blocked. A TenantAdmin's purchase lifts the guard immediately
// via the post-purchase refresh helpers (BillingPackageCard, SubscribePlanDialog,
// BillingReturnPage), but a TenantMember never lands on any of those flows — only an admin can
// buy. Without this poll a member stays on BlockedAccessPage until their own next login/refresh
// even after the tenant is unblocked. 60s comfortably exceeds the server's own 30s access cache
// (docs/design/billing-access-gate.md) rather than racing it with a tighter loop that would buy
// nothing.
const BLOCKED_POLL_INTERVAL_MS = 60_000;

/**
 * Routes that stay reachable while a tenant is Blocked: the tenant must be able to see its
 * bill, buy a package or a single app, and manage its own profile even without an active
 * subscription. Anything else falls behind {@link BlockedAccessPage}.
 *
 * Prefix match (not exact): `settings/billing` covers `settings/billing/return` too, but the
 * return route is still listed explicitly so this stays correct even if the billing root path
 * ever stops being a prefix of it.
 */
const ALLOWLISTED_PREFIXES = [
  SETTINGS_ROUTES.billing,
  SETTINGS_ROUTES.billingReturn,
  SETTINGS_ROUTES.apps,
  PROFILE_ROUTE,
] as const;

function isAllowlisted(pathname: string): boolean {
  return ALLOWLISTED_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`),
  );
}

/**
 * Sits between {@link import('./RoleGuard').RoleGuard} and `TenantLayout` (outside the sidebar
 * shell, per docs/design/billing-access-gate.md "Frontend"): a Blocked tenant on a
 * non-allowlisted route gets the full-screen {@link BlockedAccessPage} instead of the normal
 * layout tree, no sidebar included. SuperAdmin and tenant-less users always pass through;
 * `user.billing` is null for both (contract doc), so the guard condition is false by
 * construction and needs no separate role check.
 */
const SubscriptionGuard = () => {
  const user = useAuthStore((s) => s.user);
  const setBillingAccess = useAuthStore((s) => s.setBillingAccess);
  const { pathname } = useLocation();

  const isBlocked = user?.billing?.enforced === true && user.billing.accessState === 'Blocked';
  const tenantId = user?.tenantId ?? undefined;

  const poll = useBillingAccess(tenantId, {
    enabled: isBlocked,
    refetchInterval: isBlocked ? BLOCKED_POLL_INTERVAL_MS : false,
  });

  useEffect(() => {
    if (poll.data) {
      setBillingAccess(poll.data);
    }
  }, [poll.data, setBillingAccess]);

  if (isBlocked && !isAllowlisted(pathname)) {
    return (
      <Suspense fallback={<RouteSkeleton />}>
        <BlockedAccessPage />
      </Suspense>
    );
  }

  return <Outlet />;
};

export default SubscriptionGuard;
